import { Injectable, inject } from '@angular/core';
import { Params, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';

/** Query param that opens a specific credit solution tab, e.g. `/?solution=sbloc#solutions`. */
export const SOLUTION_PARAM = 'solution';

/** Smooth-scrolls to an on-page section; jumps instantly when the user prefers reduced motion. */
export function scrollToSection(sectionId: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const element = document.getElementById(sectionId);
  if (!element) {
    return false;
  }
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  return true;
}

const MAX_FRAMES = 30;

/** Retries for a few frames so a section rendered by the navigation that just finished can be found. */
export function scrollToSectionWhenReady(sectionId: string, framesLeft = MAX_FRAMES): void {
  if (scrollToSection(sectionId) || framesLeft <= 0 || typeof requestAnimationFrame === 'undefined') {
    return;
  }
  requestAnimationFrame(() => scrollToSectionWhenReady(sectionId, framesLeft - 1));
}

/**
 * Goes to a home-page section from any page. `scroll: 'manual'` stops the
 * router's scroll-to-top so it cannot race the section scroll.
 */
export async function goToHomeSection(router: Router, sectionId: string, queryParams?: Params): Promise<void> {
  await router.navigate(['/'], { fragment: sectionId, queryParams, scroll: 'manual' });
  scrollToSectionWhenReady(sectionId);
}

/** "Request Terms" intent: signed-in users go to the form, everyone else to sign in. */
@Injectable({ providedIn: 'root' })
export class RequestTermsNavigation {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  go(): void {
    this.authService.resolvedUser$.pipe(take(1)).subscribe((user) => {
      this.router.navigate([user ? '/request-terms' : '/auth']);
    });
  }
}
